import { AppointmentType, CareSponsorType, State } from "@prisma/client";

export const ONE_HOUR_MS = 60 * 60 * 1000;
export const LATE_CANCELLATION_WINDOW_HOURS = 24;

/**
 * Which appointment types care sponsorship covers at all, per
 * `CareSponsorType` — the single source of truth for eligibility (see
 * `isCareSponsorshipEligibleAppointmentTypeForSponsorType`). GUARDIAN_CONSULT
 * is covered for every sponsor type but never consumes the numeric session
 * cap — it has its own separate `GUARDIAN_CONSULT_SPONSORSHIP_CAP` (see
 * `getCanBookAdditionalSponsoredSession`). INTAKE is covered for every
 * sponsor type too; whether it's actually cap-gated for a given
 * CareSponsorship depends on both sponsor type AND that sponsorship's start
 * date — see `CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE` and
 * `getCareSponsorshipCapGatedAppointmentTypes` below (NOT this file's
 * cap-gated Record directly, which always answers as if the cutover has
 * already passed). INDIVIDUAL_THERAPY is covered (and cap-gated) for every
 * sponsor type too.
 * INSURANCE_PAYER_NETWORK_STATUS sponsorships additionally cover
 * MEDICATION_MANAGEMENT, MEDICATION_MANAGEMENT_INTAKE, and FAMILY_THERAPY
 * (Marble is really billing the payer for those, so they're cap-gated the
 * same as individual therapy); SCHOOL/SCHOOL_NETWORK sponsorships (a school
 * paying directly) don't cover them at all.
 *
 * GROUP_THERAPY is deliberately absent for every sponsor type — care
 * sponsorship does not cover groups today, and
 * `getActivelyEnrollingGroups`/`enrollStudentInGroup`
 * (apps/web/src/lib/modules/groups/actions.ts) explicitly block scheduling
 * one for any student with an active sponsorship. Adding support means
 * adding it here AND removing/reworking those two blocks.
 */
export const CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE: Record<
  CareSponsorType,
  readonly AppointmentType[]
> = {
  [CareSponsorType.SCHOOL]: [
    AppointmentType.INTAKE,
    AppointmentType.INDIVIDUAL_THERAPY,
    AppointmentType.GUARDIAN_CONSULT,
  ],
  [CareSponsorType.SCHOOL_NETWORK]: [
    AppointmentType.INTAKE,
    AppointmentType.INDIVIDUAL_THERAPY,
    AppointmentType.GUARDIAN_CONSULT,
  ],
  [CareSponsorType.INSURANCE_PAYER_NETWORK_STATUS]: [
    AppointmentType.INTAKE,
    AppointmentType.INDIVIDUAL_THERAPY,
    AppointmentType.GUARDIAN_CONSULT,
    AppointmentType.MEDICATION_MANAGEMENT,
    AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
    AppointmentType.FAMILY_THERAPY,
  ],
  [CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS]: [
    AppointmentType.INTAKE,
    AppointmentType.INDIVIDUAL_THERAPY,
    AppointmentType.GUARDIAN_CONSULT,
    AppointmentType.MEDICATION_MANAGEMENT,
    AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
    AppointmentType.FAMILY_THERAPY,
  ],
};

/**
 * The sponsor types where Marble itself is covering the care — the payer
 * relationship (Marble bills the payer as if INN) or the state uninsured
 * program — as opposed to SCHOOL/SCHOOL_NETWORK, where a school pays
 * directly. The source of truth behind `isMarbleCareSponsorship`; use this
 * array when you need to iterate/derive across both (e.g. unioning their
 * cap-gated appointment types) rather than re-listing them.
 */
export const MARBLE_CARE_SPONSOR_TYPES: readonly CareSponsorType[] = [
  CareSponsorType.INSURANCE_PAYER_NETWORK_STATUS,
  CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS,
];

/**
 * The sponsor types where a school (or network) pays for the student's care
 * directly — the complement of {@link MARBLE_CARE_SPONSOR_TYPES}, and the
 * source of truth behind `isSchoolCareSponsorType`. Use this array when you
 * need to filter or iterate across both (e.g. scoping a query to the
 * sponsorships that still get the internal "ending soon" heads-up) rather
 * than re-listing them.
 */
export const SCHOOL_CARE_SPONSOR_TYPES: readonly CareSponsorType[] = [
  CareSponsorType.SCHOOL,
  CareSponsorType.SCHOOL_NETWORK,
];

/**
 * States where a school/school-network sponsored student is scheduled with
 * supervisee (LMSW-level) therapists by default, to keep the sponsor's
 * per-session cost down. Keyed on the student's own state. Client-facing
 * scheduling only shows supervisees; admin booking defaults to supervisees
 * but can include non-supervisees. See `getCareSponsorshipSuperviseeRestriction`.
 */
export const SCHOOL_CARE_SPONSORSHIP_SUPERVISEE_DEFAULT_STATES: readonly State[] =
  [State.NY];

/**
 * Which of the eligible types above (see
 * `CARE_SPONSORSHIP_ELIGIBLE_APPOINTMENT_TYPES_BY_SPONSOR_TYPE` — every list
 * here must be a subset of that one) consume the family's numeric
 * session-cap allotment, per `CareSponsorType`. INDIVIDUAL_THERAPY counts
 * against every sponsor type. MEDICATION_MANAGEMENT,
 * MEDICATION_MANAGEMENT_INTAKE, and FAMILY_THERAPY additionally count, but
 * ONLY for the Marble sponsor types (INSURANCE_PAYER_NETWORK_STATUS and
 * MARBLE_STATE_UNINSURED_PATIENTS — see `MARBLE_CARE_SPONSOR_TYPES`), since
 * those are the only sponsor types that cover those session types at all.
 * INTAKE also counts for those same Marble sponsor types — Marble is paying
 * for the intake itself there, so it's one of the sponsorship's covered
 * sessions rather than a freebie ahead of the cap. For SCHOOL/SCHOOL_NETWORK,
 * INTAKE stays outside the cap entirely (a school paying directly still
 * gets it thrown in for free, same as GUARDIAN_CONSULT for every sponsor
 * type).
 *
 * This Record is date-independent — it's the "if the intake cutover has
 * already passed" answer. `CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE`
 * below explains why callers that decide which appointment types actually
 * consume a specific sponsorship's cap must go through
 * `getCareSponsorshipCapGatedAppointmentTypes` (which strips INTAKE back out
 * for a sponsorship that predates the cutover) rather than reading this
 * Record directly for that purpose.
 *
 * The single source of truth for cap-gating — shared by session-usage
 * computation (`getCareSponsorshipSessionUsage`) and appointment-type
 * eligibility (`isCareSponsorshipEligibleAppointmentTypeForSponsorType`) so
 * they can't drift apart.
 */
export const CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE: Record<
  CareSponsorType,
  readonly AppointmentType[]
> = {
  // note: if school and school network ever diverge, the round robin cache slug
  // for care sponsorship will need to evolve to include the care sponsor type in the slug
  // see workers/lambdas/crons/cacheTherapistSetsForRoundRobinScheduling.ts.

  // warning: if you ever update school and school network to include intakes towards the cap, you should
  // also update isIntakeCapGatedForCareSponsorship, which right now returns false for those care sponsor
  // types, or your changes won't apply
  [CareSponsorType.SCHOOL]: [AppointmentType.INDIVIDUAL_THERAPY],
  [CareSponsorType.SCHOOL_NETWORK]: [AppointmentType.INDIVIDUAL_THERAPY],
  [CareSponsorType.INSURANCE_PAYER_NETWORK_STATUS]: [
    AppointmentType.INTAKE,
    AppointmentType.INDIVIDUAL_THERAPY,
    AppointmentType.MEDICATION_MANAGEMENT,
    AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
    AppointmentType.FAMILY_THERAPY,
  ],
  [CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS]: [
    AppointmentType.INTAKE,
    AppointmentType.INDIVIDUAL_THERAPY,
    AppointmentType.MEDICATION_MANAGEMENT,
    AppointmentType.MEDICATION_MANAGEMENT_INTAKE,
    AppointmentType.FAMILY_THERAPY,
  ],
};

/**
 * The cutover for counting INTAKE toward the numeric session cap for the
 * Marble sponsor types (INSURANCE_PAYER_NETWORK_STATUS,
 * MARBLE_STATE_UNINSURED_PATIENTS — see `MARBLE_CARE_SPONSOR_TYPES`).
 *
 * `getCareSponsorshipSessionUsage` is computed live from `UserAppointment`
 * rows on every call — nothing about cap usage is stored — so INTAKE being
 * unconditionally in
 * `CARE_SPONSORSHIP_CAP_GATED_APPOINTMENT_TYPES_BY_SPONSOR_TYPE` would
 * retroactively recount every already-completed INTAKE for every existing
 * active sponsorship the moment this ships, which can immediately push
 * already-mid-treatment clients over their (unchanged) `sessionCap`,
 * triggering the CAP_REACHED cron to auto-cancel their upcoming appointments,
 * blocking their next booking, and skipping/misfiring the "ending soon"
 * heads-up. `getCareSponsorshipCapGatedAppointmentTypes` strips INTAKE back
 * out for any sponsorship whose `startedAt` predates this date.
 *
 * Gating on `CareSponsorship.startedAt` this way means every sponsorship
 * that already existed when this shipped keeps the old behavior (INTAKE
 * free of the cap) permanently — matching the terms that family/therapist
 * were already told — while every sponsorship started on/after this date
 * gets the new behavior from day one.
 *
 * Deploying the code ahead of this date is safe and has no effect until the
 * date arrives: `CareSponsorship.startedAt` always defaults to `now()` at
 * creation (no code path sets it to a future date), so no sponsorship can
 * satisfy `startedAt >= this date` until wall-clock time actually reaches
 * it — the new behavior switches on automatically then, with no further
 * deploy needed.
 */
export const CARE_SPONSORSHIP_INTAKE_CAP_GATING_CUTOVER_DATE = new Date(
  "2026-09-14T00:00:00.000Z",
);

/**
 * Why a CareSponsorship ended. Stored as a plain string on
 * `CareSponsorship.offboardedReason` (not a Prisma enum, so this can evolve
 * without a migration) — always go through this const object rather than
 * hardcoding string literals at call sites. This may change to enum in db if
 * we decide on more permanent reasons.
 */
export const CareSponsorshipEndReason = {
  /** Care Ops ends sponsorship so billing can switch to insurance — future
   * appointments must NOT be auto-cancelled; their careSponsorshipId stamp
   * is cleared so billing switches to insurance. Active sponsorship
   * otherwise supersedes OON / eligibility-pass workflows. */
  SWITCHED_TO_INSURANCE: "SWITCHED_TO_INSURANCE",
  /** Care Ops manually ended the sponsorship for a reason other than the
   * cap being reached (e.g. family moved, no longer needs care sponsorship).
   * or care completed. */
  CARE_COMPLETED: "CARE_COMPLETED",
  /** Student used their full session allotment. */
  CAP_REACHED: "CAP_REACHED",
  /** Student was referred out of Marble care entirely. */
  REFERRED_OUT: "REFERRED_OUT",
  /**
   * The student's school changed (admin update or counselor unassign) and
   * the school-based sponsorship no longer applies. Does NOT trigger
   * appointment cancellation or a client status transition — ops are
   * alerted via Slack to decide next steps.
   */
  SCHOOL_CHANGE: "SCHOOL_CHANGE",
} as const;

export type CareSponsorshipEndReason =
  (typeof CareSponsorshipEndReason)[keyof typeof CareSponsorshipEndReason];

/** Only these reasons trigger auto-cancellation of future scheduled sessions. */
export const CARE_SPONSORSHIP_END_REASONS_REQUIRING_CANCELLATION: readonly CareSponsorshipEndReason[] =
  [
    CareSponsorshipEndReason.CARE_COMPLETED,
    CareSponsorshipEndReason.CAP_REACHED,
    CareSponsorshipEndReason.REFERRED_OUT,
  ];

/**
 * First N unexcused no-shows (incl. late cancellations) are not counted
 * against the family's session cap / allotment but are still billed at the
 * normal rate (`billableToSponsor: true`, `countsTowardCap: false`). Every
 * occurrence after the Nth counts toward the cap the same as a completed
 * session.
 */
export const ALLOTMENT_EXEMPT_UNEXCUSED_NO_SHOW_COUNT = 1;

/**
 * How many GUARDIAN_CONSULT appointments a student can have attributed to a
 * single active CareSponsorship. Fixed today (no per-sponsor override, unlike
 * `sessionCap`/`defaultSessionCap`) — would move to a per-CareSponsor field
 * if a second sponsor needs a different limit.
 */
export const GUARDIAN_CONSULT_SPONSORSHIP_CAP = 1;

/**
 * Reasons that move the student to CARE_COMPLETED when the sponsorship ends
 * (via app-side `updateClientStatus`). REFERRED_OUT sets
 * StudentStatus.REFERRED_OUT instead.
 */
export const CARE_SPONSORSHIP_END_REASONS_REQUIRING_CARE_COMPLETED: readonly CareSponsorshipEndReason[] =
  [
    CareSponsorshipEndReason.CARE_COMPLETED,
    CareSponsorshipEndReason.CAP_REACHED,
  ];

/**
 * How many days ahead of a sponsored student's last covered session the
 * Care Collab "ending soon" heads-up fires for their therapist + school
 * worker.
 */
export const CARE_SPONSORSHIP_ENDING_SOON_DAYS_AHEAD = 5;

/**
 * How many calendar days after the last capped sponsored session the daily
 * cron auto-ends the sponsorship with CAP_REACHED.
 */
export const CARE_SPONSORSHIP_CAP_REACHED_END_DAYS_AFTER = 1;
